import { safeImageUrl, type Author } from '../../lib/sanity';
import { renderedImageDimensions } from '../../lib/imageDimensions';

type AuthorCardProps = {
  author: Author | null | undefined;
};

type SocialKey = 'twitter' | 'linkedin' | 'github' | 'website' | 'email';

const SOCIAL_LINKS: { key: SocialKey; label: string; href: (author: Author) => string | undefined }[] = [
  { key: 'twitter', label: 'X', href: (author) => author.twitter },
  { key: 'linkedin', label: 'LinkedIn', href: (author) => author.linkedin },
  { key: 'github', label: 'GitHub', href: (author) => author.github },
  { key: 'website', label: 'Website', href: (author) => author.website },
  { key: 'email', label: 'Email', href: (author) => (author.email ? `mailto:${author.email}` : undefined) },
];

function getAuthorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hues = [210, 250, 280, 320, 160, 30, 0];
  return `hsl(${hues[Math.abs(hash) % hues.length]} 42% 38%)`;
}

function AuthorAvatar({ author }: { author: Author }) {
  const photoUrl = safeImageUrl(author.photo, (b) => b.width(72).height(72).auto('format'));
  const dimensions = renderedImageDimensions(author.photo, 72);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        width={dimensions?.width ?? 72}
        height={dimensions?.height ?? 72}
        sizes="36px"
        className="author-avatar-sm"
      />
    );
  }

  return (
    <span
      className="author-avatar-initials"
      style={{ backgroundColor: getAvatarColor(author.name) }}
      aria-hidden="true"
    >
      {getAuthorInitials(author.name)}
    </span>
  );
}

function SocialIcon({ type }: { type: SocialKey }) {
  switch (type) {
    case 'twitter':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.45 20.45h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.354V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case 'github':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.404 1.02.005 2.04.138 3 .404 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
        </svg>
      );
    case 'website':
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      );
    case 'email':
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect width="20" height="16" x="2" y="4" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      );
  }
}

function AuthorSocialLinks({ author }: { author: Author }) {
  const links = SOCIAL_LINKS.map((social) => ({
    ...social,
    url: social.href(author),
  })).filter((social) => social.url);

  if (links.length === 0) return null;

  return (
    <span className="author-social-icons">
      {links.map((social) => (
        <a
          key={social.key}
          href={social.url}
          target={social.key === 'email' ? undefined : '_blank'}
          rel={social.key === 'email' ? undefined : 'noopener noreferrer'}
          aria-label={social.label}
          className="author-social-icon"
        >
          <SocialIcon type={social.key} />
        </a>
      ))}
    </span>
  );
}

export function AuthorCard({ author }: AuthorCardProps) {
  if (!author) return null;

  return (
    <div className="author-byline">
      <AuthorAvatar author={author} />
      <div className="author-byline-text">
        <div className="author-byline-name-row">
          <span className="author-name-sm">{author.name}</span>
          <AuthorSocialLinks author={author} />
        </div>
        {author.role && <span className="author-role-sm">{author.role}</span>}
      </div>
    </div>
  );
}
